/**
 * The obligation chain one Work item walked, and where it stopped.
 *
 * `universalVerification.work` answers "what records exist for this Work"; it
 * is addressed by a `work:<uuid>` grammar and returns whole protocol envelopes.
 * This contract answers the different, narrower question a person dogfooding
 * the product actually asks — **where did this work item stop, and why** — for
 * ANY canonical Work id the tenant actually stored, and it returns only the
 * fields that answer it.
 *
 * Three refusals this shape exists to prevent:
 *
 * 1. **A receipt is not a verdict.** `aggregateStatus` and `assurance` are
 *    separate, required, non-defaultable fields. A receipt can be signed,
 *    contract-bound, digest-bound AND carry `aggregateStatus: "NOT_RUN"` —
 *    nothing ran. Anything that counts receipts, or renders a receipt's mere
 *    existence as a tick, reports success for work that never happened.
 * 2. **A fixture is not a subject.** `subject.class` is derived from the
 *    Work's own recorded `source.uri` and is always returned WITH the uri and
 *    the rule that classified it, so the classification is auditable rather
 *    than asserted.
 * 3. **Null is not empty.** `supersedesReceiptId` / `supersedesAttemptId` are
 *    `.nullable()` and never optional, so a consumer cannot confuse "there is
 *    no predecessor" with "the predecessor was not loaded".
 */

import { z } from "zod";
import type { Operation } from "../types";

/**
 * The Work id grammar this surface accepts. Deliberately WIDER than
 * `universalWorkDetailParamsSchema`'s `work:<uuid>`: the canonical ids a tenant
 * actually stores are structured (`work:coding:universal-canary:<digest>`,
 * `work:ui-gate:89694`), and a uuid-only grammar refuses every one of them.
 * Still anchored, still prefix-bound, still length-capped.
 */
export const WORK_CHAIN_ID_PATTERN = /^work:[A-Za-z0-9][A-Za-z0-9._:#-]{0,240}$/;

export const workChainStatusSchema = z.enum(["PASS", "MISMATCH", "FAIL", "UNKNOWN", "NOT_RUN", "PENDING"]);
export const workChainAssuranceSchema = z.enum(["PROSPECTIVE", "RETROSPECTIVE"]);

/**
 * How an obligation ended up, flattened to one value the UI can order and
 * colour. `REJECTED` (admission refused it) and `NOT_DETERMINED` (admitted, but
 * the receipt carries no determination for it) are distinct from every
 * verification status — neither is a verdict, and neither may read as one.
 */
export const workChainOutcomeSchema = z.enum([
  "PASS",
  "MISMATCH",
  "FAIL",
  "UNKNOWN",
  "NOT_RUN",
  "PENDING",
  "REJECTED",
  "NOT_DETERMINED",
]);

export const workChainSubjectClassSchema = z.enum([
  "real-subject",
  "self-test-fixture",
  "synthetic-fixture",
]);
export type WorkChainSubjectClass = z.infer<typeof workChainSubjectClassSchema>;

/**
 * Classify a Work's SUBJECT from the Work's own recorded `source.uri`.
 *
 * THE single classifier for "is this the product verifying itself, a
 * synthetic fixture, or real work" — every surface that shows the
 * distinction (the per-item obligation chain, the receipts ledger, the work
 * ledger) calls this one function, so the rule cannot drift between them.
 * Pure and dependency-free by design: it lives in a contract file (imported
 * by both `excellent/server` and `excellent/client` today) rather than a
 * server-only route module specifically so every consumer can share it
 * instead of re-deriving the same fact.
 *
 * Production holds 81 receipts behind this classifier: 43 `self-test-fixture`
 * (`excellent:` scheme — the product's own canary), 36 `synthetic-fixture`
 * (`obligation:fixture` / reserved-TLD ui-gate subjects), and 2
 * `real-subject`. A screen that counts receipts without applying this never
 * shows that split.
 */
export function classifyWorkSubject(sourceUri: string): {
  class: WorkChainSubjectClass;
  label: string;
  basis: string;
} {
  const scheme = sourceUri.slice(0, Math.max(sourceUri.indexOf(":"), 0)).toLowerCase();
  if (scheme === "excellent") {
    return {
      class: "self-test-fixture",
      label: "Fixture — the system verifying itself",
      basis: 'source.uri scheme is "excellent:" (an internal self-test subject)',
    };
  }
  let host = "";
  try {
    host = new URL(sourceUri).hostname.toLowerCase();
  } catch {
    host = "";
  }
  const reservedTld = [".test", ".example", ".invalid", ".localhost"].some((suffix) =>
    host.endsWith(suffix),
  );
  if (reservedTld || host === "localhost") {
    return {
      class: "synthetic-fixture",
      label: "Fixture — synthetic test subject",
      basis: `source.uri host "${host}" is a reserved test domain (RFC 2606)`,
    };
  }
  return {
    class: "real-subject",
    label: "Real subject",
    basis: "source.uri names a subject outside the system's own fixtures",
  };
}

const workChainSubjectSchema = z
  .object({
    class: workChainSubjectClassSchema,
    label: z.string().min(1),
    /** The rule that produced `class`, quoted so a reader can check it. */
    basis: z.string().min(1),
    sourceKind: z.string().min(1),
    sourceUri: z.string().min(1),
    sourceDigest: z.string().min(1),
  })
  .strict();

const workChainOracleSchema = z
  .object({
    oracleRef: z.string().min(1),
    ok: z.boolean(),
    reasonCode: z.string().min(1),
    statement: z.string().min(1),
  })
  .strict();

export const workChainObligationSchema = z
  .object({
    obligationId: z.string().min(1),
    /** The obligation id's tail — `bounds`, `policy:1`, `replay:1`, … */
    role: z.string().min(1),
    classification: z.string().min(1).nullable(),
    required: z.boolean(),
    predicateKind: z.string().min(1),
    admission: z
      .object({
        decision: z.enum(["admitted", "rejected"]),
        reasonCodes: z.array(z.string().min(1)),
      })
      .strict()
      .nullable(),
    determination: z
      .object({
        status: workChainStatusSchema,
        reasonCodes: z.array(z.string().min(1)),
        rationale: z.string().min(1),
        authority: z.string().min(1),
        evaluatorRef: z.string().min(1),
        deterministicIntegrityPassed: z.boolean(),
        evidenceArtifactCount: z.number().int().nonnegative(),
        oracleDeterminations: z.array(workChainOracleSchema),
      })
      .strict()
      .nullable(),
    outcome: workChainOutcomeSchema,
  })
  .strict();

export const workChainReceiptSchema = z
  .object({
    receiptId: z.string().min(1),
    /** THE verdict. Never defaulted, never inferred from the receipt existing. */
    aggregateStatus: workChainStatusSchema,
    assurance: workChainAssuranceSchema,
    issuedAt: z.string().min(1),
    verifierActorId: z.string().min(1),
    attemptId: z.string().min(1),
    contractId: z.string().min(1),
    contractVersion: z.string().min(1),
    contractDigest: z.string().min(1),
    policyRef: z.string().min(1),
    signatureKeyId: z.string().min(1),
    signatureAlgorithm: z.string().min(1),
    /** null means "no predecessor recorded", never "not loaded". */
    supersedesReceiptId: z.string().min(1).nullable(),
    independenceDisclosures: z.array(z.string().min(1)),
    replay: z
      .object({
        status: z.enum(["REPLAYED", "REPLAYABLE", "BLOCKED", "NOT_AVAILABLE"]),
        reasonCodes: z.array(z.string().min(1)),
        replayedAt: z.string().min(1).nullable(),
        commands: z.array(z.string().min(1)),
        environmentDigest: z.string().min(1),
      })
      .strict(),
  })
  .strict();

export const workChainAttemptSchema = z
  .object({
    attemptId: z.string().min(1),
    state: z.string().min(1),
    workerActorId: z.string().min(1),
    openedAt: z.string().min(1),
    submittedAt: z.string().min(1).nullable(),
    outputSnapshotDigest: z.string().min(1).nullable(),
    /** null means "no predecessor recorded", never "not loaded". */
    supersedesAttemptId: z.string().min(1).nullable(),
  })
  .strict();

export const workChainStopSchema = z
  .object({
    stage: z.enum([
      "contract",
      "attempt",
      "admission",
      "determination",
      "replay",
      "verdict",
      "complete",
    ]),
    title: z.string().min(1),
    detail: z.string().min(1),
    obligationId: z.string().min(1).nullable(),
    role: z.string().min(1).nullable(),
    reasonCodes: z.array(z.string().min(1)),
  })
  .strict();

export const verificationWorkChainSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantId: z.string().min(1),
    work: z
      .object({
        id: z.string().min(1),
        title: z.string().min(1),
        description: z.string(),
        workType: z.string().min(1),
        state: z.string().min(1),
        consequence: z.string().min(1),
        projectId: z.string().min(1).nullable(),
        requestedByActorId: z.string().min(1),
        createdAt: z.string().min(1),
        updatedAt: z.string().min(1),
      })
      .strict(),
    subject: workChainSubjectSchema,
    contract: z
      .object({
        contractId: z.string().min(1),
        version: z.string().min(1),
        contractDigest: z.string().min(1),
        deploymentMode: z.string().min(1),
        acceptedAt: z.string().min(1),
        acceptedByActorId: z.string().min(1),
        obligationCount: z.number().int().nonnegative(),
      })
      .strict()
      .nullable(),
    attempts: z.array(workChainAttemptSchema),
    /** Newest first. The chain below is computed against `receipts[0]`. */
    receipts: z.array(workChainReceiptSchema),
    obligations: z.array(workChainObligationSchema),
    /** The one thing the screen exists to answer. */
    stop: workChainStopSchema,
  })
  .strict()
  .describe(
    "One Work item's obligation chain and the first stage that refused. aggregateStatus and assurance are rendered separately and explicitly: a NOT_RUN receipt is a signed record that nothing ran, not a pass.",
  );

export type VerificationWorkChain = z.infer<typeof verificationWorkChainSchema>;
export type VerificationWorkChainObligation = z.infer<typeof workChainObligationSchema>;
export type VerificationWorkChainReceipt = z.infer<typeof workChainReceiptSchema>;
export type VerificationWorkChainStop = z.infer<typeof workChainStopSchema>;

export const verificationWorkChainParamsSchema = z
  .object({
    id: z
      .string()
      .regex(WORK_CHAIN_ID_PATTERN, "expected a canonical work:<id> identifier"),
  })
  .strict();

const chain: Operation<
  z.infer<typeof verificationWorkChainParamsSchema>,
  void,
  void,
  VerificationWorkChain
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/universal/work/${encodeURIComponent(id)}/chain`,
  params: verificationWorkChainParamsSchema,
  response: verificationWorkChainSchema,
};

export const verificationWorkChain = { chain };
